import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableFooter from "@material-ui/core/TableFooter";
import TableHead from "@material-ui/core/TableHead";
import TablePagination from "@material-ui/core/TablePagination";
import TableRow from "@material-ui/core/TableRow";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  StyledTableCell,
  StyledTableRow,
  useStyles,
} from "components/profiles/ProfileStyles";
import Actions from "./CollaboratorsVacationsPeriodsTableAction";
import { GetNumberOfDays } from "utils/dates/GetNumberOfDays";
import DateUtils from "utils/dates/DateUtils";
import { compareStrings } from "utils/sort/strings";
import { createContent } from "utils/tables/createContent";

function desc(a, b, orderBy) {
  if (a[orderBy] === null || a[orderBy] === undefined) {
    a[orderBy] = "";
  }
  if (b[orderBy] === null || b[orderBy] === undefined) {
    b[orderBy] = "";
  }

  if (orderBy === "remunerationBalance" || orderBy === "enjoymentBalance") {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    } else {
      return 1;
    }
  }

  return compareStrings(a[orderBy].toString(), b[orderBy].toString());
}

const CollaboratorsVacationsPeriodsTable = props => {
  const classes = useStyles();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [rows, setRows] = useState({});
  const [rowsLength, setRowsLength] = useState(0);
  const [headers, setHeaders] = useState([]);
  const [mainField, setMainField] = useState("");
  const [visibleHeaders, setVisibleHeaders] = useState([]);
  const [sortedFields, setSortedFields] = useState([]);
  const [order, setOrder] = React.useState("asc");
  const [orderBy, setOrderBy] = React.useState("date");
  const [t] = useTranslation();

  const getType = value => {
    switch (value) {
      case "newrhmodule.vacation.EnjoyedPeriod":
        return t("HR.enjoyment");
      case "newrhmodule.vacation.PaidPeriod":
        return t("HR.remuneration");
      case "newrhmodule.vacation.DemissionPeriod":
        return t("HR.demission");
      case "newrhmodule.vacation.VacationPurchase":
        return t("HR.vacationPurchase");
      case "newrhmodule.vacation.Utilization":
        return t("HR.utilization");
      case "newrhmodule.vacation.UtilizationWithoutSoldVacationDays":
        return t("HR.utilizationWithoutSoldVacationDays");
      case "newrhmodule.vacation.UtilizationWithSoldVacationDays":
        return t("HR.utilizationWithSoldVacationDays");
      case "newrhmodule.vacation.DemissionPeriodRemuneration":
        return t("HR.demissionRemuneration");
      case "newrhmodule.vacation.DemissionPeriodEnjoyment":
        return t("HR.demissionEnjoyment");
      default:
        break;
    }
  };

  React.useEffect(() => {
    setHeaders(props.headers);
    setRows(props.rows);
    setRowsLength(props.rows.length);
    setMainField(props.mainField);
    setVisibleHeaders(props.visibleHeaders);
    setSortedFields(props.sortedFields);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.headers, props.mainField, props.rows, props.visibleHeaders]);

  function handlePageChange(event, newPage) {
    setPage(newPage);
  }
  const handleRowsPerPageChange = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (event, property) => {
    const isDesc = orderBy === sortedFields[property] && order === "desc";
    setOrder(isDesc ? "asc" : "desc");
    setOrderBy(sortedFields[property]);
  };

  const UsersTableHead = props => {
    const { order, orderBy, onRequestSort } = props;
    const [t] = useTranslation();
    const paginationTop = action => e => {
      var selector = "[aria-label^='" + action + "']";
      document.querySelector(selector).click();
      e.preventDefault();
    };
    const createSortHandler = property => event => {
      onRequestSort(event, property);
    };

    return (
      <TableHead>
        <TableRow>
          {visibleHeaders.map((header, index) => (
            <StyledTableCell
              key={index}
              size="small"
              align="left"
              sortDirection={orderBy === sortedFields[index] ? order : false}
            >
              <TableSortLabel
                active={orderBy === sortedFields[index]}
                direction={order}
                onClick={createSortHandler(index)}
                disabled={index === 4}
              >
                {header.toUpperCase()}
                {orderBy === sortedFields[index] ? (
                  <span className={classes.visuallyHidden}>
                    {order === "desc"
                      ? "sorted descending"
                      : "sorted ascending"}
                  </span>
                ) : null}
              </TableSortLabel>
            </StyledTableCell>
          ))}
        </TableRow>
      </TableHead>
    );
  };

  UsersTableHead.propTypes = {
    onRequestSort: PropTypes.func.isRequired,
    order: PropTypes.oneOf(["asc", "desc"]).isRequired,
    orderBy: PropTypes.string.isRequired,
  };

  React.useEffect(() => {
    if (props.toFirstPage) {
      document.getElementById("toFirstPage").click();
    }
  }, [props.rows, props.toFirstPage]);

  const toFirstPage = () => {
    setPage(0);
  };

  const getDurationDateString = (startDateString, endDateString) => {
    const startDate = DateUtils.parse(startDateString);
    const endDate = DateUtils.parse(endDateString);
    const dayCount = GetNumberOfDays(startDate, endDate);
    const dayString = dayCount > 1 ? t("GENERAL.days") : t("GENERAL.day");
    return `${DateUtils.format(startDate, "DD/MM/YYYY")} - ${DateUtils.format(
      endDate,
      "DD/MM/YYYY"
    )} (${dayCount} ${dayString})`;
  };

  return (
    <Paper className={classes.root}>
      <div className={classes.tableWrapper}>
        <Table className={classes.table} size="small">
          {rows.length > 0 && (
            <UsersTableHead
              classes={classes}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
            />
          )}
          <TableBody>
            {createContent(rows, headers, order, orderBy, desc)
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map(row => (
                <StyledTableRow key={row.contractId}>
                  <TableCell size="small" style={{ width: "20%" }}>
                    {row["class"]
                      ? row["documentFilePath"]
                        ? getType(row["class"])
                        : getType(row["class"]) + " (Agendamento)"
                      : "-"}
                  </TableCell>
                  <TableCell size="small" style={{ width: "30%" }}>
                    {row["class"] === "newrhmodule.vacation.Utilization" ||
                    row["class"] ===
                      "newrhmodule.vacation.UtilizationWithoutSoldVacationDays" ||
                    row["class"] ===
                      "newrhmodule.vacation.UtilizationWithSoldVacationDays" ||
                    row["class"] === "newrhmodule.vacation.EnjoyedPeriod" ||
                    row["class"] === "newrhmodule.vacation.PaidPeriod" ||
                    row["class"] === "newrhmodule.vacation.VacationPurchase"
                      ? getDurationDateString(row["startDate"], row["endDate"])
                      : "-"}
                  </TableCell>
                  <TableCell size="small" style={{ width: "20%" }}>
                    {row["date"]
                      ? DateUtils.formatString(row["date"], "DD/MM/YYYY")
                      : "-"}
                  </TableCell>
                  <TableCell size="small" style={{ width: "20%" }}>
                    {row["registredBy"] ?? "-"}
                  </TableCell>
                  <TableCell
                    size="small"
                    align="right"
                    style={{ width: "15%" }}
                  >
                    <div
                      style={{
                        marginTop: "auto",
                        marginBottom: "auto",
                        display: "flex",
                        flexWrap: "nowrap ",
                        justifyContent: "flex-end",
                      }}
                    >
                      <Actions
                        item={row}
                        name={row.name}
                        permission={props.permission}
                        fetchData={props.fetchData}
                        periodValues={props.periodValues}
                        vacationsPeriods={props.vacationsPeriods}
                        getType={getType}
                        context={props.context}
                        ruleset={props.ruleset}
                      />
                    </div>
                  </TableCell>
                </StyledTableRow>
              ))}
          </TableBody>
        </Table>
        <button hidden id="toFirstPage" onClick={toFirstPage}></button>
        {rows.length > 0 && (
          <TableFooter style={{ float: "right" }}>
            <TablePagination
              labelRowsPerPage={"Resultados por página"}
              count={rowsLength}
              rowsPerPage={rowsPerPage}
              page={page}
              component="div"
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          </TableFooter>
        )}
      </div>
    </Paper>
  );
};

CollaboratorsVacationsPeriodsTable.propTypes = {
  rows: PropTypes.array.isRequired,
  headers: PropTypes.array.isRequired,
  visibleHeaders: PropTypes.array.isRequired,
  mainField: PropTypes.string.isRequired,
};

export default CollaboratorsVacationsPeriodsTable;
